import { useState, useEffect, ReactElement, ReactNode } from "react";
import classnamesBind from "classnames/bind";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import { ProductImage } from "../../types/productTypes/product";
import styles from "./AiImageDisclosure.scss";

const classnames = classnamesBind.bind(styles);

const AI_ASSET_TYPE = "Ai Generated";

const SHORT_LABEL_KEY = "pdp_image_disclaimer_short";
const FULL_LABEL_KEY = "pdp_image_disclaimer_long";

interface AiImageDisclosureProps {
  image: Pick<ProductImage, "url" | "aiAssetType">;
  ariaHidden?: boolean;
  children: ReactNode;
}

const AiImageDisclosure = ({
  image,
  ariaHidden = false,
  children,
}: AiImageDisclosureProps): ReactElement => {
  const { features, getTranslation } = useRuntimeConstants() as {
    features?: { aiImageDisclosureEnabled?: boolean };
    getTranslation: (key: string) => string;
  };
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (ariaHidden) {
      setIsExpanded(false);
    }
  }, [ariaHidden]);

  useEffect(() => {
    setIsExpanded(false);
  }, [image.url]);

  const isAiGenerated = image.aiAssetType === AI_ASSET_TYPE;

  if (!features?.aiImageDisclosureEnabled || !isAiGenerated) {
    return <>{children}</>;
  }

  const shortLabel = getTranslation(SHORT_LABEL_KEY);
  const fullLabel = getTranslation(FULL_LABEL_KEY);

  const toggleExpanded = () => setIsExpanded((expanded) => !expanded);

  return (
    <div
      className={styles.container}
      data-testid="ai-image-disclosure-container"
    >
      {children}
      <div
        className={classnames(styles.aiImageDisclosure, {
          [styles.expanded]: isExpanded,
        })}
        data-testid="ai-image-disclosure"
        role="button"
        aria-hidden={ariaHidden}
        tabIndex={ariaHidden ? -1 : 0}
        aria-expanded={isExpanded}
        onClick={toggleExpanded}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            toggleExpanded();
          }
        }}
        onBlur={() => setIsExpanded(false)}
      >
        <span aria-hidden="true">
          <span className={styles.label}>{shortLabel}</span>
          <span className={styles.expandedMessage}>{fullLabel}</span>
        </span>
        <span className={styles.visuallyHidden}>{fullLabel}</span>
      </div>
    </div>
  );
};

export default AiImageDisclosure;
